import { z } from "zod";

import { NodeRole } from "@skilitics/nautilus-core/schemas";

import { designerNodeDataSchema } from "../designerNode/index.ts";
import { multilingualNodeConfigSchema, slotIdSchema } from "../shared/index.ts";
import { triggerActionSchemas } from "./triggerActionSchema.ts";

export const addNodeActionSchema = z.object({
  type: z.literal("ADD"),
  payload: z.object({
    node: designerNodeDataSchema,
    slotId: slotIdSchema.optional(),
  }),
});

export type AddNodeAction = z.infer<typeof addNodeActionSchema>;

export const updateNodeActionSchema = z.object({
  type: z.literal("UPDATE_CONFIG"),
  payload: z.object({
    id: z.number(),
    config: multilingualNodeConfigSchema,
  }),
});

export type UpdateNodeConfigAction = z.infer<typeof updateNodeActionSchema>;

export const replaceNodeActionSchema = z.object({
  type: z.literal("REPLACE"),
  payload: z.object({
    id: z.number(),
    replacement: designerNodeDataSchema,
    /**
     * Declares the replacement stands in for the replaced node, so references to it
     * follow the replacement rather than clearing: set by node migrations.
     */
    repointRefs: z.boolean().optional(),
  }),
});

export type ReplaceNodeAction = z.infer<typeof replaceNodeActionSchema>;

export const removeNodeActionSchema = z.object({
  type: z.literal("REMOVE"),
  payload: z.object({
    id: z.number(),
  }),
});

export type RemoveNodeAction = z.infer<typeof removeNodeActionSchema>;

export const updateVersionsActionSchema = z.object({
  type: z.literal("UPDATE_VERSIONS"),
  payload: z.object({
    versions: z.array(z.tuple([z.string(), z.string()])),
  }),
});

export type UpdateVersionsAction = z.infer<typeof updateVersionsActionSchema>;

export const setOwnerActionSchema = z.object({
  type: z.literal("SET_OWNER"),
  payload: z.object({
    id: z.number(),
    owner: z.enum(["ai", "user"]).optional(),
  }),
});

export type SetOwnerAction = z.infer<typeof setOwnerActionSchema>;

export const moveNodeActionSchema = z.object({
  type: z.literal("MOVE"),
  payload: z.object({
    from: slotIdSchema,
    to: slotIdSchema,
  }),
});

export type MoveNodeAction = z.infer<typeof moveNodeActionSchema>;

export const reorderNodeActionSchema = z.object({
  type: z.literal("REORDER"),
  payload: z.object({
    id: z.number(),
    newIndex: z.number().nonnegative(),
  }),
});

export type ReorderNodeAction = z.infer<typeof reorderNodeActionSchema>;

export const addMultiValueSlotActionSchema = z.object({
  type: z.literal("ADD_MULTI_VALUE_SLOT"),
  payload: z.object({
    slotId: slotIdSchema,
  }),
});

export type AddMultiValueSlotAction = z.infer<typeof addMultiValueSlotActionSchema>;

export const changeNodeTypeIdActionSchema = z.object({
  type: z.literal("CHANGE_TYPE_ID"),
  payload: z.object({
    oldTypeId: z.string(),
    newTypeId: z.string(),
  }),
});

export type ChangeNodeTypeIdAction = z.infer<typeof changeNodeTypeIdActionSchema>;

export const setRoleActionSchema = z.object({
  type: z.literal("SET_ROLE"),
  payload: z.object({
    id: z.number(),
    role: NodeRole,
  }),
});

export type SetRoleAction = z.infer<typeof setRoleActionSchema>;

export const removeLanguageActionSchema = z.object({
  type: z.literal("REMOVE_LANGUAGE"),
  payload: z.object({
    lang: z.string(),
  }),
});

export type RemoveLanguageAction = z.infer<typeof removeLanguageActionSchema>;

export const nodeActionSchema = z.discriminatedUnion("type", [
  addMultiValueSlotActionSchema,
  addNodeActionSchema,
  changeNodeTypeIdActionSchema,
  moveNodeActionSchema,
  removeNodeActionSchema,
  reorderNodeActionSchema,
  replaceNodeActionSchema,
  setOwnerActionSchema,
  setRoleActionSchema,
  updateNodeActionSchema,
  updateVersionsActionSchema,
  removeLanguageActionSchema,
  ...triggerActionSchemas,
]);

export type NodeAction = z.infer<typeof nodeActionSchema>;
